/**
 * Custom hook for calculations in UsageStats
 *
 * Extracted from UsageStats.tsx
 */

import { useMemo } from 'react';
import type {
  TimingBreakdown,
  TimingRow,
  UseUsageStatsCalculationsProps,
  UseUsageStatsCalculationsReturn,
} from '../types/stats.types';

const TIMING_LABEL_OVERRIDES: Record<string, string> = {
  total_pipeline_ms: 'Total pipeline',
  total_ms: 'Total análisis',
  resolve_documents_ms: 'Carga de documentos',
  resolve_document_paths_ms: 'Resolución de rutas',
  citation_analyzer_total_ms: 'Total Citation Analyzer',
  load_chunks_ms: 'Cargar chunks',
  hybrid_retrieval_ms: 'Búsqueda híbrida',
  mmr_ms: 'Diversidad (MMR)',
  rerank_ms: 'Reordenamiento',
  add_context_neighbors_ms: 'Agregar vecinos',
  prepare_messages_ms: 'Preparar mensajes',
  llm_completion_ms: 'LLM',
  cost_calculation_ms: 'Calcular costos',
  citation_mapping_ms: 'Mapear citas',
  retrieval_stats_ms: 'Estadísticas de retrieval',
};

const humanizeKey = (key: string): string =>
  key
    .replace(/_ms$/, '')
    .replace(/_/g, ' ')
    .replace(/\b(\w)/g, match => match.toUpperCase());

const getTimingLabel = (key: string, depth: number): string => {
  if (key === 'total_ms') {
    return depth === 0 ? 'Total análisis' : 'Total';
  }

  return TIMING_LABEL_OVERRIDES[key] || humanizeKey(key);
};

const buildTimingRows = (data?: TimingBreakdown, depth = 0): TimingRow[] => {
  if (!data) {
    return [];
  }

  const entries = Object.entries(data).filter(([, value]) => value !== null && value !== undefined);
  if (entries.length === 0) {
    return [];
  }

  const totals = entries.filter(([key]) => key === 'total_ms' || key === 'total_pipeline_ms');
  const summaryTotals = entries.filter(
    ([key, value]) =>
      typeof value === 'number' &&
      key.endsWith('_total_ms') &&
      key !== 'total_ms' &&
      key !== 'total_pipeline_ms'
  );
  const numericOthers = entries.filter(
    ([key, value]) =>
      typeof value === 'number' &&
      key !== 'total_ms' &&
      key !== 'total_pipeline_ms' &&
      !key.endsWith('_total_ms')
  );
  const nested = entries.filter(
    ([, value]) => value && typeof value === 'object' && !Array.isArray(value)
  ) as Array<[string, TimingBreakdown]>;

  const ordered: Array<[string, number | TimingBreakdown | undefined]> = [
    ...totals,
    ...summaryTotals,
    ...numericOthers,
    ...nested,
  ];

  return ordered.flatMap(([key, value]) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return [
        {
          id: `${depth}-${key}`,
          label: getTimingLabel(key, depth),
          value,
          depth,
        },
      ];
    }

    if (value && typeof value === 'object') {
      const children = buildTimingRows(value, depth + 1);
      if (children.length === 0) {
        return [];
      }

      return [
        {
          id: `${depth}-${key}-group`,
          label: getTimingLabel(key, depth),
          depth,
          isGroup: true,
        },
        ...children,
      ];
    }

    return [];
  });
};

/**
 * Manages timing calculations and transformations
 */
export function useUsageStatsCalculations({
  timingBreakdown,
}: UseUsageStatsCalculationsProps): UseUsageStatsCalculationsReturn {
  const timingRows = useMemo(() => buildTimingRows(timingBreakdown), [timingBreakdown]);

  const totalPipelineMs = useMemo(() => {
    return typeof timingBreakdown?.total_pipeline_ms === 'number'
      ? timingBreakdown.total_pipeline_ms
      : typeof timingBreakdown?.total_ms === 'number'
      ? timingBreakdown.total_ms
      : undefined;
  }, [timingBreakdown]);

  const totalPipelineSeconds = useMemo(() => {
    return totalPipelineMs ? totalPipelineMs / 1000 : undefined;
  }, [totalPipelineMs]);

  const timingSummaryLabel = useMemo(() => {
    if (!totalPipelineMs) return '';
    const formatDuration = (ms: number): string => {
      if (ms >= 1000) {
        const seconds = ms / 1000;
        return `${seconds >= 10 ? seconds.toFixed(1) : seconds.toFixed(2)}s`;
      }
      return `${Math.round(ms)}ms`;
    };
    return `Total pipeline • ${formatDuration(totalPipelineMs)}`;
  }, [totalPipelineMs]);

  return {
    timingRows,
    totalPipelineMs,
    totalPipelineSeconds,
    timingSummaryLabel,
    getTimingLabel,
  };
}
